import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Phone, SearchIcon, SplitVertical, UserIcon } from "@/components/icons";
import { Button, Input, Skeleton, useDebounceCallback } from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { OrdersListPagination } from "@/modules/orders/presentation/ui/OrdersListPagination";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useAddressOptionsQuery,
  useCustomersQuery,
} from "../api/queries";

const DEFAULT_LIMIT = 20;
const ALL_VALUE = "__all__";

export function CustomersPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission("orders:write");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [risk, setRisk] = useState<"" | "cod_return">("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const debouncedSearch = useDebounceCallback((v: string) => setSearch(v), 400);

  const isSearching = search.trim().length >= 2;
  const listQuery = useCustomersQuery({
    q: isSearching ? search.trim() : undefined,
    province: province || undefined,
    district: district || undefined,
    risk: risk || undefined,
    limit,
    offset,
  });

  const provinces = useAddressOptionsQuery({ level: "province" });
  const districts = useAddressOptionsQuery(
    { level: "district", province },
    Boolean(province),
  );

  const provinceOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "ທຸກແຂວງ" },
      ...(provinces.data?.data ?? []).map((row) => ({
        value: row.value,
        label: `${row.value} (${row.count})`,
      })),
    ],
    [provinces.data?.data],
  );

  const districtOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "ທຸກເມືອງ" },
      ...(districts.data?.data ?? []).map((row) => ({
        value: row.value,
        label: `${row.value} (${row.count})`,
      })),
    ],
    [districts.data?.data],
  );

  useEffect(() => {
    setOffset(0);
  }, [search, province, district, risk]);

  const rows = listQuery.data?.data ?? [];
  const totalCount = listQuery.data?.meta.total ?? 0;
  const isLoading = listQuery.isLoading;
  const hasFilters =
    isSearching || Boolean(province) || Boolean(district) || Boolean(risk);

  const riskOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: "ທຸກລູກຄ້າ" },
      {
        value: "cod_return",
        label: "ເຄີຍສົ່ງຄືນ COD",
      },
    ],
    [],
  );

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ລູກຄ້າ
            </h2>
            <p className="text-muted-foreground text-sm">
              ຄົ້ນຫາເບີໂທ ຫຼື ຊື່ — ກອງຕາມແຂວງ/ເມືອງ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          {canWrite ? (
            <Button
              variant="outline"
              className="rounded-full border-secondary/40"
              onClick={() => nav({ to: "/app/customers/merge" })}
            >
              <SplitVertical data-icon="inline-start" />
              Merge ລູກຄ້າ
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              placeholder="ຄົ້ນຫາ ຊື່ / ເບີໂທ..."
              className="pl-9"
              autoComplete="off"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SimpleSelect
              value={province || ALL_VALUE}
              onValueChange={(value) => {
                const next = value === ALL_VALUE ? "" : value;
                setProvince(next);
                setDistrict("");
              }}
              options={provinceOptions}
              placeholder="ແຂວງ"
            />
            <SimpleSelect
              value={district || ALL_VALUE}
              onValueChange={(value) =>
                setDistrict(value === ALL_VALUE ? "" : value)
              }
              options={districtOptions}
              placeholder="ເມືອງ"
              disabled={!province}
            />
            <SimpleSelect
              value={risk || ALL_VALUE}
              onValueChange={(value) =>
                setRisk(value === "cod_return" ? "cod_return" : "")
              }
              options={riskOptions}
              placeholder="ຄວາມສ່ຽງ"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-muted-foreground text-xs uppercase tracking-wide">
            {hasFilters ? "ຜົນການຄົ້ນຫາ" : "ອັບເດດລ່າສຸດ"}
          </p>

          {isLoading ? (
            <div className="flex flex-col gap-2">
              {["one", "two", "three", "four", "five"].map((id) => (
                <Skeleton key={id} className="h-16 rounded-2xl" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="brand-card p-8 text-center text-muted-foreground text-sm">
              {hasFilters ? "ບໍ່ພົບລູກຄ້າ" : "ຍັງບໍ່ມີລູກຄ້າໃນລະບົບ"}
            </div>
          ) : (
            rows.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() =>
                  nav({ to: "/app/customers/$id", params: { id: c.id } })
                }
                className={cn(
                  "brand-card flex items-center gap-3 p-4 text-left transition-colors hover:bg-muted/30",
                )}
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-secondary">
                  <UserIcon className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{c.name}</span>
                    {c.province ? (
                      <span className="text-muted-foreground text-xs">
                        {[c.district, c.province].filter(Boolean).join(" · ")}
                      </span>
                    ) : null}
                  </div>
                  {c.phone ? (
                    <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                      <Phone className="size-3.5" />
                      {c.phone}
                    </span>
                  ) : null}
                </div>
                <span className="shrink-0 text-muted-foreground text-xs">
                  {formatDateTimeLocal(c.updatedAt)}
                </span>
              </button>
            ))
          )}

          <OrdersListPagination
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            isLoading={isLoading}
            onChange={(nextOffset, nextLimit) => {
              setOffset(nextOffset);
              setLimit(nextLimit);
            }}
          />
        </div>
      </Main>
    </>
  );
}
